import React, { useState, useEffect, useCallback } from 'react'
import {
  DocumentTextIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  EyeIcon,
  TrashIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  DocumentMagnifyingGlassIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline'
import { invoicesAPI, InvoiceListItem, InvoiceDetail, financeiroAPI } from '@/services/api'

const InvoiceOCRPage: React.FC = () => {
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDetail | null>(null)
  const [previewData, setPreviewData] = useState<any>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [categories, setCategories] = useState<any[]>([])
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('')

  // Confirm form state
  const [confirmForm, setConfirmForm] = useState({
    due_date: '',
    description: '',
    category_id: '',
    notes: ''
  })

  // Reject reason
  const [rejectReason, setRejectReason] = useState('')

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false)

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true)
      const params: any = { limit: 100 }
      if (statusFilter) params.status = statusFilter
      const data = await invoicesAPI.list(params)
      setInvoices(data.invoices || [])
    } catch (err: any) {
      console.error('Erro ao carregar notas:', err)
      setError('Erro ao carregar notas fiscais')
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  useEffect(() => {
    loadInvoices()
    loadCategories()
  }, [loadInvoices])

  const loadCategories = async () => {
    try {
      const data = await financeiroAPI.categorias.list()
      setCategories(data)
    } catch (err) {
      console.error('Erro ao carregar categorias:', err)
    }
  }

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return

    setUploading(true)
    setError(null)

    for (const file of Array.from(files)) {
      try {
        const result = await invoicesAPI.upload(file)
        console.log('Upload result:', result)
      } catch (err: any) {
        console.error('Erro no upload:', err)
        setError(`Erro ao enviar ${file.name}: ${err.response?.data?.detail || err.message}`)
      }
    }

    setUploading(false)
    loadInvoices()
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleFileUpload(e.dataTransfer.files)
  }

  const viewInvoiceDetails = async (invoiceId: number) => {
    try {
      const data = await invoicesAPI.get(invoiceId)
      setSelectedInvoice(data)
    } catch (err: any) {
      setError('Erro ao carregar detalhes da nota')
    }
  }

  const openConfirmModal = async (invoice: InvoiceListItem) => {
    try {
      const preview = await invoicesAPI.preview(invoice.id)
      setPreviewData(preview)
      setConfirmForm({
        due_date: preview.bill_preview?.issue_date || new Date().toISOString().split('T')[0],
        description: preview.bill_preview?.description || '',
        category_id: '',
        notes: ''
      })
      setShowConfirmModal(true)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao carregar preview')
    }
  }

  const handleConfirm = async () => {
    if (!previewData) return

    try {
      await invoicesAPI.confirm(previewData.invoice_id, {
        due_date: confirmForm.due_date,
        description: confirmForm.description || undefined,
        category_id: confirmForm.category_id ? parseInt(confirmForm.category_id) : undefined,
        notes: confirmForm.notes || undefined
      })
      setShowConfirmModal(false)
      setPreviewData(null)
      loadInvoices()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao confirmar nota')
    }
  }

  const openRejectModal = (invoice: InvoiceListItem) => {
    setSelectedInvoice({ ...invoice } as any)
    setRejectReason('')
    setShowRejectModal(true)
  }

  const handleReject = async () => {
    if (!selectedInvoice || !rejectReason) return

    try {
      await invoicesAPI.reject(selectedInvoice.id, rejectReason)
      setShowRejectModal(false)
      setSelectedInvoice(null)
      loadInvoices()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao rejeitar nota')
    }
  }

  const handleDelete = async (invoiceId: number) => {
    if (!confirm('Tem certeza que deseja excluir esta nota?')) return

    try {
      await invoicesAPI.delete(invoiceId)
      loadInvoices()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao excluir nota')
    }
  }

  const formatCurrency = (value: string | number | undefined) => {
    if (!value) return 'R$ 0,00'
    const num = typeof value === 'string' ? parseFloat(value) : value
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(num)
  }

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
      'uploaded': { color: 'bg-gray-100 text-gray-800', icon: <ClockIcon className="w-3 h-3" />, label: 'Enviado' },
      'processing': { color: 'bg-blue-100 text-blue-800', icon: <ArrowPathIcon className="w-3 h-3 animate-spin" />, label: 'Processando' },
      'processed': { color: 'bg-green-100 text-green-800', icon: <CheckCircleIcon className="w-3 h-3" />, label: 'Processado' },
      'error': { color: 'bg-red-100 text-red-800', icon: <XCircleIcon className="w-3 h-3" />, label: 'Erro' },
      'confirmed': { color: 'bg-purple-100 text-purple-800', icon: <CheckCircleIcon className="w-3 h-3" />, label: 'Confirmado' },
      'rejected': { color: 'bg-orange-100 text-orange-800', icon: <XCircleIcon className="w-3 h-3" />, label: 'Rejeitado' }
    }

    const badge = badges[status] || badges['uploaded']
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${badge.color} flex items-center gap-1`}>
        {badge.icon}
        {badge.label}
      </span>
    )
  }

  const getFileTypeIcon = (fileType: string) => {
    if (fileType === 'nfe') return <span className="text-green-600 font-bold">XML</span>
    if (fileType === 'danfe' || fileType === 'pdf') return <span className="text-red-600 font-bold">PDF</span>
    return <span className="text-blue-600 font-bold">IMG</span>
  }

  if (loading && invoices.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <DocumentMagnifyingGlassIcon className="w-8 h-8 text-blue-600" />
          OCR de Notas Fiscais
        </h1>
        <p className="text-gray-600 mt-1">Importe notas fiscais (XML, PDF, Imagem) e converta em Contas a Pagar automaticamente</p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
          <span className="text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
            <XCircleIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 mb-8 transition-colors ${
          isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="text-center">
          <CloudArrowUpIcon className={`w-16 h-16 mx-auto mb-4 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {uploading ? 'Enviando...' : 'Arraste arquivos aqui ou clique para selecionar'}
          </h3>
          <p className="text-gray-600 mb-4">Suporta: XML (NF-e), PDF (DANFE), Imagens (JPG, PNG)</p>
          <input
            type="file"
            id="file-upload"
            className="hidden"
            accept=".xml,.pdf,.jpg,.jpeg,.png"
            multiple
            onChange={(e) => handleFileUpload(e.target.files)}
            disabled={uploading}
          />
          <label
            htmlFor="file-upload"
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg cursor-pointer ${
              uploading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {uploading ? (
              <>
                <ArrowPathIcon className="w-5 h-5 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <CloudArrowUpIcon className="w-5 h-5" />
                Selecionar Arquivos
              </>
            )}
          </label>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Filtrar por Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todos</option>
            <option value="uploaded">Enviado</option>
            <option value="processing">Processando</option>
            <option value="processed">Processado</option>
            <option value="error">Erro</option>
            <option value="confirmed">Confirmado</option>
            <option value="rejected">Rejeitado</option>
          </select>
          <button
            onClick={loadInvoices}
            className="ml-auto flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg"
          >
            <ArrowPathIcon className="w-5 h-5" />
            Atualizar
          </button>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Arquivo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Numero NF
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fornecedor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Confianca
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acoes
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    <DocumentTextIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Nenhuma nota fiscal encontrada</p>
                    <p className="text-sm">Envie arquivos XML, PDF ou imagens para processar</p>
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {getFileTypeIcon(invoice.file_type)}
                        <span className="text-sm text-gray-900 truncate max-w-[200px]" title={invoice.file_name}>
                          {invoice.file_name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {invoice.invoice_number || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {invoice.issuer_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {formatCurrency(invoice.total_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {invoice.confidence_score !== undefined ? (
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                invoice.confidence_score >= 80
                                  ? 'bg-green-500'
                                  : invoice.confidence_score >= 50
                                  ? 'bg-yellow-500'
                                  : 'bg-red-500'
                              }`}
                              style={{ width: `${invoice.confidence_score}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600">{invoice.confidence_score.toFixed(0)}%</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getStatusBadge(invoice.status)}
                        {invoice.needs_review && (
                          <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500" title="Necessita revisao" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => viewInvoiceDetails(invoice.id)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="Ver detalhes"
                        >
                          <EyeIcon className="w-5 h-5" />
                        </button>
                        {invoice.status === 'processed' && (
                          <>
                            <button
                              onClick={() => openConfirmModal(invoice)}
                              className="text-green-600 hover:text-green-900 p-1"
                              title="Confirmar e criar conta a pagar"
                            >
                              <CheckCircleIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => openRejectModal(invoice)}
                              className="text-orange-600 hover:text-orange-900 p-1"
                              title="Rejeitar"
                            >
                              <XCircleIcon className="w-5 h-5" />
                            </button>
                          </>
                        )}
                        {['uploaded', 'error', 'rejected'].includes(invoice.status) && (
                          <button
                            onClick={() => handleDelete(invoice.id)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Excluir"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invoice Detail Modal */}
      {selectedInvoice && !showConfirmModal && !showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Detalhes da Nota Fiscal</h3>
                <button onClick={() => setSelectedInvoice(null)} className="text-gray-400 hover:text-gray-600">
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-500">Arquivo</label>
                  <p className="font-medium">{selectedInvoice.file_name}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Tipo</label>
                  <p className="font-medium">{selectedInvoice.file_type.toUpperCase()}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Numero NF</label>
                  <p className="font-medium">{selectedInvoice.invoice_number || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Chave NFe</label>
                  <p className="font-medium text-xs truncate" title={selectedInvoice.nfe_key}>{selectedInvoice.nfe_key || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Fornecedor</label>
                  <p className="font-medium">{selectedInvoice.issuer_name || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">CNPJ</label>
                  <p className="font-medium">{selectedInvoice.issuer_cnpj || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Data Emissao</label>
                  <p className="font-medium">
                    {selectedInvoice.issue_date
                      ? new Date(selectedInvoice.issue_date).toLocaleDateString('pt-BR')
                      : '-'}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Valor Total</label>
                  <p className="font-medium text-lg text-green-600">{formatCurrency(selectedInvoice.total_amount)}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Confianca OCR</label>
                  <p className="font-medium">{selectedInvoice.confidence_score?.toFixed(1)}%</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Tempo Processamento</label>
                  <p className="font-medium">{selectedInvoice.processing_duration_ms}ms</p>
                </div>
              </div>

              {selectedInvoice.error_message && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 text-sm">{selectedInvoice.error_message}</p>
                </div>
              )}

              {selectedInvoice.extracted_data && (
                <div>
                  <label className="text-sm text-gray-500 block mb-2">Dados Extraidos (JSON)</label>
                  <pre className="bg-gray-100 p-4 rounded-lg text-xs overflow-x-auto max-h-48">
                    {JSON.stringify(selectedInvoice.extracted_data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setSelectedInvoice(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirmModal && previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Confirmar Nota e Criar Conta a Pagar</h3>
            </div>
            <div className="p-6 space-y-6">
              {/* Warnings */}
              {previewData.warnings?.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-800 mb-2 flex items-center gap-2">
                    <ExclamationTriangleIcon className="w-5 h-5" />
                    Atencao
                  </h4>
                  <ul className="text-sm text-yellow-700 list-disc list-inside">
                    {previewData.warnings.map((w: string, i: number) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Preview Data */}
              <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2">
                  <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <label className="text-xs text-gray-500">Documento</label>
                    <p className="font-medium">{previewData.bill_preview?.document_number || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <CurrencyDollarIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <label className="text-xs text-gray-500">Valor</label>
                    <p className="font-medium text-green-600">{formatCurrency(previewData.bill_preview?.total_amount)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 col-span-2">
                  <BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <label className="text-xs text-gray-500">Fornecedor</label>
                    <p className="font-medium">{previewData.supplier?.name || '-'}</p>
                    <p className="text-xs text-gray-500">{previewData.supplier?.cnpj}</p>
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <CalendarDaysIcon className="w-4 h-4 inline mr-1" />
                    Data de Vencimento *
                  </label>
                  <input
                    type="date"
                    value={confirmForm.due_date}
                    onChange={(e) => setConfirmForm({ ...confirmForm, due_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descricao</label>
                  <input
                    type="text"
                    value={confirmForm.description}
                    onChange={(e) => setConfirmForm({ ...confirmForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder={previewData.bill_preview?.description}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                  <select
                    value={confirmForm.category_id}
                    onChange={(e) => setConfirmForm({ ...confirmForm, category_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecione uma categoria</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name || cat.nome}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observacoes</label>
                  <textarea
                    value={confirmForm.notes}
                    onChange={(e) => setConfirmForm({ ...confirmForm, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows={2}
                  />
                </div>
              </div>

              {/* Items Preview */}
              {previewData.items?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Itens da Nota ({previewData.items_count})</h4>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Produto</th>
                          <th className="px-3 py-2 text-right">Qtd</th>
                          <th className="px-3 py-2 text-right">Valor Unit.</th>
                          <th className="px-3 py-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {previewData.items.map((item: any, i: number) => (
                          <tr key={i}>
                            <td className="px-3 py-2">{item.description || item.name}</td>
                            <td className="px-3 py-2 text-right">{item.quantity}</td>
                            <td className="px-3 py-2 text-right">{formatCurrency(item.unit_price || item.unit_value)}</td>
                            <td className="px-3 py-2 text-right">{formatCurrency(item.total || item.total_value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowConfirmModal(false)
                  setPreviewData(null)
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirm}
                disabled={!confirmForm.due_date}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <CheckCircleIcon className="w-5 h-5" />
                Confirmar e Criar Conta a Pagar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Rejeitar Nota Fiscal</h3>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Motivo da Rejeicao *</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder="Informe o motivo da rejeicao..."
                required
              />
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowRejectModal(false)
                  setSelectedInvoice(null)
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Rejeitar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default InvoiceOCRPage
